import React, { useState } from 'react';
import WebhookDeliveryLog from './WebhookDeliveryLog';
import WebhookEndpointForm from './WebhookEndpointForm';
import WebhookEndpointsList from './WebhookEndpointsList';
import WebhookSecret from './WebhookSecret';
import WebhookTestSend from './WebhookTestSend';
import { useWebhooks } from './useWebhooks';
import { IWebhookEndpoint } from '../../../../networked-shared-interfaces/community-admin/external-vendor-webhooks';

type Mode = { kind: 'list' } | { kind: 'form'; endpointId: string | null } | { kind: 'manage'; endpointId: string };

/** Webhooks v2 for one community: list, add/edit form, and a per-endpoint secret / test / delivery-log panel. */
const WebhooksSection = ({ communityId }: { communityId: string }) => {
	const api = useWebhooks(communityId);
	const { list, catalog } = api;
	const [mode, setMode] = useState<Mode>({ kind: 'list' });
	const back = () => setMode({ kind: 'list' });

	if (!list || !catalog) {
		return api.loading
			? <div className="d-flex justify-content-center"><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div>
			: <div className="alert alert-warning">Could not load webhook endpoints. <button type="button" className="btn btn-link p-0" onClick={api.refresh}>Retry</button></div>;
	}

	const endpoints = list.items;
	const selected = 'endpointId' in mode ? endpoints.find((e) => e.id === mode.endpointId) ?? null : null;
	// An endpoint that vanished (deleted elsewhere) falls back to the list.
	const view = mode.kind !== 'list' && mode.endpointId && !selected ? 'list' : mode.kind;

	const remove = (e: IWebhookEndpoint) => {
		if (window.confirm('Delete this webhook endpoint? Its delivery log is removed too.')) api.remove(e.id);
	};

	if (view === 'form' && mode.kind === 'form') {
		return (
			<WebhookEndpointForm
				key={mode.endpointId ?? 'new'}
				endpoint={selected}
				catalog={catalog}
				isSaving={api.saving}
				onVerify={api.verifyUrl}
				onCancel={back}
				onSubmit={async (payload) => {
					const ok = selected ? await api.update({ id: selected.id, ...payload }) : await api.create(payload);
					if (ok) back();
				}}
			/>
		);
	}

	if (view === 'manage' && selected) {
		return (
			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-center mb-3">
						<h5 className="mb-0">{selected.name || selected.url}</h5>
						<button type="button" className="btn btn-secondary btn-sm" onClick={back}>Back to endpoints</button>
					</div>
					<WebhookSecret endpointId={selected.id} secret={selected.secret} onRoll={api.rollSecret} />
					<WebhookTestSend api={api} endpointId={selected.id} events={catalog.events} />
					<WebhookDeliveryLog api={api} endpointId={selected.id} events={catalog.events} />
				</div>
			</div>
		);
	}

	return (
		<WebhookEndpointsList
			endpoints={endpoints}
			maxEndpoints={list.maxEndpoints}
			busyId={api.busyId}
			onAdd={() => setMode({ kind: 'form', endpointId: null })}
			onEdit={(e) => setMode({ kind: 'form', endpointId: e.id })}
			onOpen={(e) => setMode({ kind: 'manage', endpointId: e.id })}
			onToggle={(e) => api.update({ id: e.id, enabled: e.status !== 'active' }, true)}
			onDelete={remove}
		/>
	);
};

export default WebhooksSection;
